import { observer } from 'mobx-react-lite';
import React, { useEffect, useState } from 'react';
import history from '../../../browserHistory';
import useStores from '../../../hooks/use-store';
import { formatDateTime } from '../../../shared/utils/dateTime';
import { getPercentage } from '../../../stores/utils';
import { DashboardButtonPopup } from '../ListItemSummary/DashboardButtonPopup';
import { ItemTitle, ListItemSummary } from '../index';
import {
  ColumnOneContainer,
  ColumnThreeContainer,
  ColumnTwoContainer,
  Container,
  List,
  T1,
  T2,
  T3,
  TitleContainer,
} from './Styles';
import LaneMediaUploads from './TotalMediaUploads/LaneMediaUploads';
import TotalMediaUploads from './TotalMediaUploads/TotalMediaUploads';
import LaneProductsStatused from './TotalProductsStatused/LaneProductsStatused';
import TotalProductsStatused from './TotalProductsStatused/TotalProductsStatused';
import {
  formatCommas,
  getComponentNameFromStorage,
  getMediaUploadButtonColor,
  getProductStatusButtonColor,
  loadProxyBidAndUserCountList,
} from './common';

const DashboardCard = observer(
  ({
    auctionID,
    endTime,
    itemName,
    laneMetricList,
    onClick,
    productNonStatusedCount = 0,
    productStatusedCount = 0,
    productTotalCount = 0,
    proxyBidCount,
    registeredMembers,
    registeredUsers,
    startTime,
    totalItems,
    totalItemsError,
    totalItemsPending,
    totalItemsUploaded = 0,
    uniqueUsers,
  }) => {
    const {
      DashboardStore: { auctionMetricList, setDashboardCardView },
    } = useStores();

    const [componentName, setComponentName] = useState(
      getComponentNameFromStorage(auctionID),
    );

    const firstColumnButtonList = loadProxyBidAndUserCountList(
      formatCommas(proxyBidCount),
      formatCommas(registeredMembers),
      formatCommas(registeredUsers),
      formatCommas(uniqueUsers),
      auctionID,
      itemName,
    );

    const handleClick = (item) => {
      onClick();
      const state = { auctionName: itemName, laneName: item?.laneName };

      if (item?.laneID) {
        history.push(
          `/auction/${auctionID}/lane/${item.laneID}/products`,
          state,
        );
        return;
      }
      history.push(`/auction/${auctionID}/products`, state);
    };

    const numberOfStatusedProducts = auctionMetricList.filter(
      (a) => a.auctionID === auctionID,
    )[0]?.productStatusedCount;

    const chartViews = {
      mediaUpload: {
        buttonSettings: {
          aliasText: 'Media Uploads',
          auctionID,
          auctionName: itemName,
          color: getMediaUploadButtonColor(
            totalItemsUploaded,
            totalItems,
            totalItemsError,
          ),
          helpText: `The total number media uploads in the auction.`,
          iconClass: 'image',
          popupPosition: 'left center',
          qty: `${getPercentage(totalItemsUploaded, totalItems)}%`,
          text: `Media Uploads`,
        },
        column2: (
          <TotalMediaUploads
            auctionID={auctionID}
            itemName={itemName}
            key={`auction-media-upload-${totalItemsUploaded}`}
            totalItems={totalItems}
            totalItemsError={totalItemsError}
            totalItemsPending={totalItemsPending}
            totalItemsUploaded={totalItemsUploaded}
          />
        ),
        column3: (
          <LaneMediaUploads
            auctionID={auctionID}
            itemName={itemName}
            key={`lane-media-upload-${totalItemsUploaded}`}
            laneMetricList={laneMetricList}
            onClick={handleClick}
          />
        ),
        storageValue: 'mediaUpload',
      },
      productStatus: {
        buttonSettings: {
          aliasText: 'Statused Products',
          auctionID,
          auctionName: itemName,
          color: getProductStatusButtonColor(
            productStatusedCount,
            productTotalCount,
          ),
          helpText: `The total number products statused in the auction.`,
          iconClass: 'motorcycle',
          popupPosition: 'left center',
          qty: `${getPercentage(productStatusedCount, productTotalCount)}%`,
          text: `Statused Products`,
        },
        column2: (
          <TotalProductsStatused
            auctionID={auctionID}
            itemName={itemName}
            key={`auction-product-status-${numberOfStatusedProducts}`}
            productNonStatusedCount={productNonStatusedCount}
            productStatusedCount={numberOfStatusedProducts}
            productTotalCount={productTotalCount}
          />
        ),
        column3: (
          <LaneProductsStatused
            auctionID={auctionID}
            itemName={itemName}
            key={`lane-product-status-${numberOfStatusedProducts}`}
            laneMetricList={laneMetricList}
            onClick={handleClick}
            startTime={formatDateTime(startTime, 'YYYY-MM-DD HH:mm:ss')}
          />
        ),
        storageValue: 'productStatus',
      },
    };
    const componentToUse = (componentText) => {
      if (componentText === 'productStatus') return chartViews.productStatus;
      if (componentText === 'mediaUpload') return chartViews.mediaUpload;
      throw `Invalid component type - ${componentText}`;
    };

    useEffect(() => {}, [componentName]);

    return (
      <Container data-testid={`dashboard-auction-container-${itemName}`}>
        <TitleContainer>
          <T1>
            <ItemTitle
              onClick={() => handleClick()}
              testId="auction-name"
              textSize={1.5}
              titleText={itemName}
            />
          </T1>
          <T2>
            <ItemTitle
              testId="auction-start-time"
              textSize={1.2}
              titleText={`Start Time: ${formatDateTime(startTime, 'hh:mm A')}`}
            />
          </T2>
          <T3>
            <ItemTitle
              testId="auction-end-time"
              textSize={1.2}
              titleText={`End Time: ${formatDateTime(endTime, 'hh:mm A')}`}
            />
          </T3>
        </TitleContainer>
        <ColumnOneContainer>
          <ListItemSummary
            itemList={firstColumnButtonList}
            key={`px_${auctionID}`}
          />
          <List>
            <DashboardButtonPopup
              item={chartViews.mediaUpload.buttonSettings}
              key={`btn-media-upload-${totalItemsUploaded}`}
              onClick={() => {
                setComponentName(chartViews.mediaUpload.storageValue);
                setDashboardCardView(
                  auctionID,
                  chartViews.mediaUpload.storageValue,
                );
              }}
              testId={chartViews.mediaUpload.buttonSettings.testId}
            />
            <DashboardButtonPopup
              item={chartViews.productStatus.buttonSettings}
              key={`btn-product-status-${productStatusedCount}`}
              onClick={() => {
                setComponentName(chartViews.productStatus.storageValue);
                setDashboardCardView(
                  auctionID,
                  chartViews.productStatus.storageValue,
                );
              }}
              testId={chartViews.productStatus.buttonSettings.testId}
            />
          </List>
        </ColumnOneContainer>
        <ColumnTwoContainer>
          {componentToUse(componentName).column2}
        </ColumnTwoContainer>
        <ColumnThreeContainer>
          {componentToUse(componentName).column3}
        </ColumnThreeContainer>
      </Container>
    );
  },
);

export default DashboardCard;
